import React, { useEffect, useState } from "react";
import { getActiveMaintenanceNotice } from "../../services/MaintenanceService";

const SEVERITY_CONFIG = {
  info: {
    bg: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)",
    accent: "rgba(255,255,255,0.15)",
    border: "rgba(255,255,255,0.2)",
    pill: "rgba(255,255,255,0.18)",
    pillText: "#fff",
    text: "#ffffff",
    icon: "🛠️",
    label: "System Notice",
  },
  warning: {
    bg: "linear-gradient(135deg, #f59e0b 0%, #b45309 100%)",
    accent: "rgba(255,255,255,0.12)",
    border: "rgba(255,255,255,0.2)",
    pill: "rgba(255,255,255,0.2)",
    pillText: "#fff",
    text: "#ffffff",
    icon: "⚠️",
    label: "Warning",
  },
  critical: {
    bg: "linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)",
    accent: "rgba(255,255,255,0.12)",
    border: "rgba(255,255,255,0.2)",
    pill: "rgba(255,255,255,0.18)",
    pillText: "#fff",
    text: "#ffffff",
    icon: "🚨",
    label: "Critical Alert",
  },
};

const DISMISS_KEY = (id) => `maintenance_dismissed_${id}`;

const fmt = (isoStr) =>
  new Date(isoStr).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }) + " IST";

const MaintenanceBanner = () => {
  const [notice, setNotice] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    getActiveMaintenanceNotice(token)
      .then((res) => {
        const activeNotice = res?.data;
        if (!activeNotice) return;

        const alreadyDismissed = sessionStorage.getItem(DISMISS_KEY(activeNotice.id));
        if (alreadyDismissed) return;

        setNotice(activeNotice);
      })
      .catch((err) => {
        console.warn("[MaintenanceBanner] Failed to fetch:", err);
      });
  }, []);

  const handleDismiss = () => {
    if (!notice) return;
    sessionStorage.setItem(DISMISS_KEY(notice.id), "true");
    setDismissed(true);
  };

  if (!notice || dismissed) return null;

  const cfg = SEVERITY_CONFIG[notice.severity] || SEVERITY_CONFIG.info;

  return (
    <div
      id="maintenance-banner"
      role="alert"
      aria-live="assertive"
      style={{
        width: "100%",
        background: cfg.bg,
        color: cfg.text,
        boxSizing: "border-box",
        fontFamily: "'Inter', 'Roboto', -apple-system, BlinkMacSystemFont, sans-serif",
        boxShadow: "0 3px 12px rgba(0,0,0,0.22)",
        flexShrink: 0,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Subtle decorative background rings */}
      <div style={{
        position: "absolute", top: "-30px", right: "60px",
        width: "120px", height: "120px", borderRadius: "50%",
        background: "rgba(255,255,255,0.05)", pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", bottom: "-20px", right: "200px",
        width: "80px", height: "80px", borderRadius: "50%",
        background: "rgba(255,255,255,0.04)", pointerEvents: "none",
      }} />

      {/* Inner content — centred */}
      <div style={{
        maxWidth: "860px",
        margin: "0 auto",
        padding: "12px 52px 12px 20px",   /* right padding leaves space for dismiss btn */
        textAlign: "center",
        position: "relative",
      }}>

        {/* Severity pill */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginBottom: "5px" }}>
          <span style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            background: cfg.pill,
            border: `1px solid ${cfg.border}`,
            borderRadius: "20px",
            padding: "2px 10px",
            fontSize: "10px",
            fontWeight: 700,
            letterSpacing: "1px",
            textTransform: "uppercase",
            opacity: 0.95,
          }}>
            <span>{cfg.icon}</span>
            {cfg.label}
          </span>
        </div>

        {/* Title */}
        <div style={{
          fontWeight: 700,
          fontSize: "14px",
          letterSpacing: "0.2px",
          marginBottom: "4px",
          lineHeight: 1.3,
        }}>
          {notice.title}
        </div>

        {/* Message */}
        <div style={{
          fontSize: "12.5px",
          opacity: 0.88,
          lineHeight: 1.5,
          whiteSpace: "pre-line",
          marginBottom: (notice.start_time || notice.end_time) ? "6px" : "0",
        }}>
          {notice.message}
        </div>

        {/* Schedule */}
        {(notice.start_time || notice.end_time) && (
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            background: "rgba(0,0,0,0.18)",
            borderRadius: "8px",
            padding: "3px 10px",
            fontSize: "11px",
            opacity: 0.9,
          }}>
            <span>🕐</span>
            {notice.start_time && (
              <span>From <strong style={{ fontWeight: 600 }}>{fmt(notice.start_time)}</strong></span>
            )}
            {notice.start_time && notice.end_time && (
              <span style={{ opacity: 0.5 }}>→</span>
            )}
            {notice.end_time && (
              <span>Until <strong style={{ fontWeight: 600 }}>{fmt(notice.end_time)}</strong></span>
            )}
          </div>
        )}
      </div>

      {/* Dismiss button — absolute top-right */}
      {notice.is_dismissible && (
        <button
          id="maintenance-banner-close"
          onClick={handleDismiss}
          aria-label="Dismiss maintenance notification"
          style={{
            position: "absolute",
            top: "50%",
            right: "16px",
            transform: "translateY(-50%)",
            background: "rgba(255,255,255,0.15)",
            border: "1px solid rgba(255,255,255,0.3)",
            borderRadius: "50%",
            width: "26px",
            height: "26px",
            cursor: "pointer",
            color: "#fff",
            fontSize: "13px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            lineHeight: 1,
            transition: "background 0.15s ease",
            flexShrink: 0,
          }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.28)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
        >
          ✕
        </button>
      )}
    </div>
  );
};

export default MaintenanceBanner;
